import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { extractErrorMessage } from "@/api/client";
import {
  AGENT_RESEARCH_ERROR,
  agentResearchErrorDetails,
  listingAgentResearchApi,
  type ListingAgentResearchFindingDto,
  type ListingAgentResearchMatchFactDto,
  type ListingAgentResearchRunDto,
} from "@/api/listingAgentResearch";
import { Button, StatusBadge, SubTable, Timestamp, type SubTableColumn } from "@/design-system/components/ui";
import { listingAgentResearchKeys } from "@/hooks/buyers/listingAgentResearchKeys";
import { DetailSection } from "../../detailBits";
import {
  CONFIDENCE,
  FAST_RUN_SHAPE,
  MATCH_FACT_LABEL,
  MATCH_RESULT,
  ORIGIN,
  ROW_FINDING_LABEL,
  SOURCE_KIND_LABEL,
  STEP_LABEL,
  agentResearchCopy,
} from "./agentResearchCopy";
import { SafeLink } from "./StartStage";

/** "Listing page found" evidence (D14): each fact on file against the page. */
const MATCH_COLUMNS: SubTableColumn<ListingAgentResearchMatchFactDto>[] = [
  { key: "fact", header: "Fact", render: (m) => MATCH_FACT_LABEL[m.fact] ?? m.fact },
  { key: "on-file", header: "On file", render: (m) => m.onFile ?? "—" },
  { key: "found", header: "On the page", render: (m) => m.found ?? "—" },
  {
    key: "result",
    header: "Result",
    render: (m) => {
      const r = MATCH_RESULT[m.result] ?? { label: m.result, tone: "neutral" as const };
      return <StatusBadge tone={r.tone}>{r.label}</StatusBadge>;
    },
  },
];

/** A decision refusal in the reader's words — by code, never by message. */
function decisionErrorText(e: unknown): string {
  const code = agentResearchErrorDetails(e)?.code;
  if (code === AGENT_RESEARCH_ERROR.conflictingAcceptedFinding) return agentResearchCopy.conflictingAccepted;
  if (code === AGENT_RESEARCH_ERROR.findingAlreadySaved) return agentResearchCopy.alreadySaved;
  return extractErrorMessage(e);
}

/**
 * ③ Findings (D13, D17) — grouped by field, conflicts shown together, each with
 * its confidence, its sources and how it was found. ACCEPT / REJECT are for
 * Listings.Manage holders only (absent otherwise, never greyed); a finding the
 * server will not accept says why. Then the dead ends and the suggested next
 * steps.
 *
 * <p>A fast run's groups are the server's Name / Email / Phone / Firm rows; each
 * finding keeps its real field, so a row names a finding whose field differs
 * from its own (the phone's type). Older runs render as they always did.</p>
 */
export function FindingsStage({
  listingId,
  run,
  canManage,
}: {
  listingId: string;
  run: ListingAgentResearchRunDto | null | undefined;
  canManage: boolean;
}) {
  if (!run) {
    return <p className="text-small text-gray-mid m-0">{agentResearchCopy.noRunYet}</p>;
  }
  const isFast = run.shape === FAST_RUN_SHAPE;

  return (
    <div className="grid gap-space-5" data-testid="agent-research-findings">
      {run.findingGroups.length === 0 ? (
        <p className="text-small text-gray-mid m-0" data-testid="agent-research-no-findings">
          {run.isActive ? "Nothing found yet — the run is still going." : agentResearchCopy.noFindings}
        </p>
      ) : (
        run.findingGroups.map((g) => (
          <DetailSection key={g.field} title={g.fieldLabel}>
            <div className="grid gap-space-2" data-testid={`agent-research-group-${g.field}`}>
              {g.hasConflict && (
                <p className="text-small text-status-warning-ink m-0" data-testid={`agent-research-conflict-${g.field}`}>
                  These findings disagree. Accept the right one and reject the others.
                </p>
              )}
              {g.findings.map((f) => (
                <FindingCard
                  key={f.id}
                  listingId={listingId}
                  finding={f}
                  canManage={canManage}
                  rowLabel={isFast && f.field !== g.field ? ROW_FINDING_LABEL[f.field] : undefined}
                />
              ))}
            </div>
          </DetailSection>
        ))
      )}

      {run.deadEnds.length > 0 && (
        <DetailSection title="Dead ends">
          <ul className="m-0 grid list-none gap-space-2 p-0" data-testid="agent-research-dead-ends">
            {run.deadEnds.map((d, i) => (
              <li key={`${d.step}-${i}`} className="grid gap-space-1">
                <span className="text-caption uppercase tracking-wide text-gray-mid">{STEP_LABEL[d.step] ?? d.step}</span>
                {d.url && (
                  <span className="text-small">
                    <SafeLink url={d.url} />
                  </span>
                )}
                <span className="text-small text-onyx">{d.what}</span>
              </li>
            ))}
          </ul>
        </DetailSection>
      )}

      {run.suggestedNextSteps.length > 0 && (
        <DetailSection title="Suggested next steps">
          <ul className="m-0 grid gap-space-1 pl-space-4" data-testid="agent-research-next-steps">
            {run.suggestedNextSteps.map((s) => (
              <li key={s} className="text-small text-onyx">
                {s}
              </li>
            ))}
          </ul>
        </DetailSection>
      )}
    </div>
  );
}

function FindingCard({
  listingId,
  finding: f,
  canManage,
  rowLabel,
}: {
  listingId: string;
  finding: ListingAgentResearchFindingDto;
  canManage: boolean;
  /** Names the finding inside a fast run's row ("Phone type"). */
  rowLabel?: string;
}) {
  const qc = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const decide = useMutation({
    mutationFn: (decision: "Accepted" | "Rejected") => listingAgentResearchApi.decide(listingId, f.id, decision),
    onSuccess: () => {
      setProblem(null);
      void qc.invalidateQueries({ queryKey: listingAgentResearchKeys.forListing(listingId) });
    },
    onError: (e) => setProblem(decisionErrorText(e)),
  });
  const confidence = CONFIDENCE[f.confidence] ?? { label: f.confidence, tone: "neutral" as const };
  const saved = Boolean(f.savedAtUtc);

  return (
    <div
      className="grid gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2"
      data-testid={`agent-research-finding-${f.id}`}
    >
      <div className="flex flex-wrap items-center gap-space-2">
        {rowLabel && (
          <span className="text-caption uppercase tracking-wide text-gray-mid" data-testid={`agent-research-finding-label-${f.id}`}>
            {rowLabel}
          </span>
        )}
        <span className="break-all text-small font-semibold text-onyx" data-testid={`agent-research-finding-value-${f.id}`}>
          {f.value}
        </span>
        <StatusBadge tone={confidence.tone}>{confidence.label}</StatusBadge>
        <span className="text-caption text-gray-mid">{ORIGIN[f.origin] ?? f.origin}</span>
        {f.carriedFromEarlierRun && <span className="text-caption text-gray-mid">From an earlier run</span>}
        <span className="ml-auto">
          <DecisionBadge finding={f} />
        </span>
      </div>
      {f.howFound && <p className="text-caption text-gray-mid m-0">{f.howFound}</p>}

      {f.sources.length > 0 && (
        <ul className="m-0 grid list-none gap-space-1 p-0">
          {f.sources.map((s, i) => (
            <li key={i} className="grid gap-space-1 text-caption text-gray-mid">
              <span>
                {SOURCE_KIND_LABEL[s.kind] && `${SOURCE_KIND_LABEL[s.kind]}${s.title || s.url ? " · " : ""}`}
                {s.title ? `${s.title} · ` : ""}
                <SafeLink url={s.url} />
                {s.retrievedAtUtc && (
                  <>
                    {" · read "}
                    <Timestamp value={s.retrievedAtUtc} />
                  </>
                )}
              </span>
              {s.excerpt && <q className="text-onyx">{s.excerpt}</q>}
            </li>
          ))}
        </ul>
      )}

      {f.matchEvidence && (
        <div className="grid gap-space-1" data-testid={`agent-research-match-${f.id}`}>
          <span className="text-caption font-medium text-onyx">
            {f.matchEvidence.isValidListingPage ? "Listing page found — checked against the listing" : "Page checked against the listing"}
          </span>
          <SubTable<ListingAgentResearchMatchFactDto>
            caption="Listing page checked against the listing"
            rows={f.matchEvidence.facts}
            rowKey={(m) => m.fact}
            columns={MATCH_COLUMNS}
          />
        </div>
      )}

      {canManage && !saved && (
        <div className="grid gap-space-1">
          <div className="flex flex-wrap items-center gap-space-2">
            <Button
              size="sm"
              variant={f.decision === "Accepted" ? "primary" : "secondary"}
              onClick={() => decide.mutate("Accepted")}
              disabled={decide.isPending || Boolean(f.acceptBlockedReason) || f.decision === "Accepted"}
              data-testid={`agent-research-accept-${f.id}`}
            >
              Accept
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => decide.mutate("Rejected")}
              disabled={decide.isPending || f.decision === "Rejected"}
              data-testid={`agent-research-reject-${f.id}`}
            >
              Reject
            </Button>
          </div>
          {f.acceptBlockedReason && (
            <p className="text-caption text-gray-mid m-0" data-testid={`agent-research-accept-blocked-${f.id}`}>
              {f.acceptBlockedReason}
            </p>
          )}
          {problem && (
            <p className="text-caption text-status-error m-0" role="alert" data-testid={`agent-research-decision-error-${f.id}`}>
              {problem}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DecisionBadge({ finding: f }: { finding: ListingAgentResearchFindingDto }) {
  if (f.savedAtUtc) return <StatusBadge tone="ok">Saved</StatusBadge>;
  if (f.decision === "Accepted") return <StatusBadge tone="ok">Accepted</StatusBadge>;
  if (f.decision === "Rejected") return <StatusBadge tone="muted">Rejected</StatusBadge>;
  return <StatusBadge tone="neutral">To review</StatusBadge>;
}
